'use client';

import { type FC } from 'react';
import { motion, type Variants } from 'framer-motion';
import {
  MasonryGallerySingle,
  MasonryGalleryDouble,
  MasonryGalleryTriple,
  type MasonryGalleryImage,
} from '@/components/Work/Masonry';

type GalleryLayout = 'single' | 'double' | 'triple';

interface GalleryRowProps {
  layout: GalleryLayout;
  images: MasonryGalleryImage[];
  shapedImages?: boolean;
  onImageClick?: (image: MasonryGalleryImage) => void;
}

const layoutComponentMap: Record<
  GalleryLayout,
  FC<{ images: MasonryGalleryImage[]; className?: string; onImageClick?: (image: MasonryGalleryImage) => void }>
> = {
  single: MasonryGallerySingle,
  double: MasonryGalleryDouble,
  triple: MasonryGalleryTriple,
};

const simpleGridConfig: Record<GalleryLayout, { grid: string; aspects: string[] }> = {
  single: { grid: 'grid-cols-1', aspects: ['aspect-[16/9]'] },
  double: {
    grid: 'grid-cols-1 md:grid-cols-2',
    aspects: ['aspect-[4/3]', 'aspect-[4/3]'],
  },
  triple: {
    grid: 'grid-cols-1 md:grid-cols-3',
    aspects: ['aspect-[3/4]', 'aspect-[3/4]', 'aspect-[3/4]'],
  },
};

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.14,
      delayChildren: 0.1,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 36, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.7,
      ease: [0.19, 1, 0.22, 1],
    },
  },
};

export const GalleryRow: FC<GalleryRowProps> = ({ layout, images, shapedImages = true, onImageClick }) => {
  if (shapedImages) {
    const Component = layoutComponentMap[layout] ?? MasonryGallerySingle;
    return <Component images={images} className="w-full" onImageClick={onImageClick} />;
  }

  const config = simpleGridConfig[layout] ?? simpleGridConfig.single;
  const itemCount = Math.min(images.length, config.aspects.length);

  return (
    <motion.div
      className={`grid gap-3 md:gap-4 ${config.grid}`}
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-100px' }}
    >
      {images.slice(0, itemCount).map((image, index) => {
        const aspectClass = config.aspects[index] ?? 'aspect-[4/3]';

        const isVideo =
          image.kind === 'video' ||
          Boolean(image.sources?.some((source) => source.type?.toLowerCase().includes('video'))) ||
          (typeof image.src === 'string' && (
            image.src.toLowerCase().endsWith('.mp4') ||
            image.src.toLowerCase().endsWith('.webm') ||
            image.src.toLowerCase().endsWith('.mov')
          ));

        const isInteractive = Boolean(onImageClick) && !isVideo;

        return (
          <motion.figure
            key={`${image.src}-${index}`}
            variants={itemVariants}
            className={`group relative overflow-hidden rounded-3xl bg-transparent ${aspectClass} ${
              isInteractive ? 'cursor-zoom-in' : 'cursor-pointer'
            }`}
            style={{ backgroundColor: 'transparent' }}
            onClick={isInteractive ? () => onImageClick?.(image) : undefined}
            onKeyDown={
              isInteractive
                ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onImageClick?.(image);
                    }
                  }
                : undefined
            }
            role={isInteractive ? 'button' : undefined}
            tabIndex={isInteractive ? 0 : undefined}
          >
            {isVideo ? (
              <video
                className="size-full object-cover object-center transition-transform duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.04]"
                autoPlay={image.autoPlay ?? true}
                loop={image.loop ?? true}
                muted={image.muted ?? true}
                playsInline={image.playsInline ?? true}
                aria-label={image.alt}
              >
                {(image.sources && image.sources.length > 0 ? image.sources : [{ src: image.src }]).map(
                  ({ src, type }) => (
                    <source key={src} src={src} type={type} />
                  ),
                )}
              </video>
            ) : (
              <img
                src={image.src}
                alt={image.alt}
                className="size-full object-cover object-center transition-transform duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.04]"
                loading="lazy"
                decoding="async"
              />
            )}
            {image.caption ? (
              <>
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-1/2 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-95 transition-opacity duration-300 md:opacity-0 md:group-hover:opacity-100"
                />
                <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] px-4 pb-4 pt-6 text-sm text-white/90 opacity-100 transition-opacity duration-300 md:opacity-0 md:group-hover:opacity-100">
                  {image.caption}
                </figcaption>
              </>
            ) : null}
          </motion.figure>
        );
      })}
    </motion.div>
  );
};

export default GalleryRow;
